import { Select, TextInput } from "flowbite-react";
import { CustomSelect } from "../Select/CustomSelect";
import { useTranslation } from "react-i18next";
import {
  FeatureDefinition,
  FeatureValue,
  VehicleRequirement,
} from "../../api/vehicleFeatures";
export const featureInput =
  "w-full rounded-lg border border-border bg-surface-input px-3 py-2 text-sm text-text focus:border-brand focus:ring-brand disabled:opacity-60";

export function FeatureValueInput({
  definition: d,
  value,
  onChange,
  disabled = false,
  required = false,
}: {
  definition: FeatureDefinition;
  value?: FeatureValue;
  onChange: (v?: FeatureValue) => void;
  disabled?: boolean;
  required?: boolean;
}) {
  const { t } = useTranslation("workspace");
  if (d.type === "boolean" || d.type === "select")
    return (
      <Select
        aria-label={d.name}
        disabled={disabled}
        value={value === undefined ? "" : String(value)}
        onChange={(e) =>
          onChange(
            e.target.value === ""
              ? undefined
              : d.type === "boolean"
                ? e.target.value === "true"
                : e.target.value,
          )
        }
      >
        {!required && <option value="">{t("features.unset")}</option>}
        {d.type === "boolean" ? (
          <>
            <option value="true">{t("features.yes")}</option>
            <option value="false">{t("features.no")}</option>
          </>
        ) : (
          d.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))
        )}
      </Select>
    );
  return (
    <TextInput
      aria-label={d.name}
      disabled={disabled}
      type={d.type === "number" ? "number" : "text"}
      step="any"
      maxLength={500}
      value={value === undefined ? "" : String(value)}
      onChange={(e) =>
        onChange(
          e.target.value === ""
            ? undefined
            : d.type === "number"
              ? Number(e.target.value)
              : e.target.value,
        )
      }
    />
  );
}

export function RequirementFields({
  definitions,
  value,
  onChange,
  disabled = false,
}: {
  definitions: FeatureDefinition[];
  value: VehicleRequirement[];
  onChange: (v: VehicleRequirement[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("workspace");
  const available = definitions.filter(
    (d) =>
      !d.archived &&
      d.type !== "string" &&
      !value.some((r) => r.attributeId === d.id),
  );
  const update = (index: number, patch: Partial<VehicleRequirement>) =>
    onChange(value.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-3">
      {value.map((r, i) => {
        const d = definitions.find((d) => d.id === r.attributeId);
        return (
          <div
            key={r.attributeId}
            className="rounded-lg border border-border bg-surface p-3"
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-sm font-medium">
                {d?.name || r.attributeId}
                {d?.unit ? ` (${d.unit})` : ""}
                {d?.archived ? ` · ${t("features.archived")}` : ""}
              </span>
              <button
                type="button"
                disabled={disabled}
                className="text-sm text-muted underline"
                onClick={() => onChange(value.filter((_, j) => i !== j))}
              >
                {t("features.remove")}
              </button>
            </div>
            {d && (
              <div className="grid gap-2 sm:grid-cols-2">
                <select
                  aria-label={t("features.comparison")}
                  className={featureInput}
                  disabled={disabled || d.archived}
                  value={r.operator}
                  onChange={(e) =>
                    update(i, {
                      operator: e.target
                        .value as VehicleRequirement["operator"],
                    })
                  }
                >
                  {(d.type === "number" ? ["gte", "lte", "eq"] : ["eq"]).map(
                    (op) => (
                      <option key={op} value={op}>
                        {t(`features.${op}`)}
                      </option>
                    ),
                  )}
                </select>
                <FeatureValueInput
                  definition={d}
                  required
                  value={r.value}
                  disabled={disabled || d.archived}
                  onChange={(v) => {
                    update(i, { value: v === undefined ? "" : v });
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
      {!disabled && available.length > 0 && (
        <CustomSelect
          // Each addition consumes an option; reset the trigger to "Add requirement".
          key={value.map((r) => r.attributeId).join(",")}
          type="radio"
          label={t("features.addRequirement")}
          items={available.map((d) => ({
            key: d.id,
            value: d.id,
            label: d.name,
          }))}
          onSelectChanged={(selected) => {
            const d = available.find((d) => d.id === selected);
            if (d)
              onChange([
                ...value,
                {
                  attributeId: d.id,
                  operator: d.type === "number" ? "gte" : "eq",
                  value:
                    d.type === "boolean"
                      ? true
                      : d.type === "number"
                        ? ""
                        : d.options[0].id,
                },
              ]);
          }}
        />
      )}
      {!value.length && (
        <p className="text-sm text-muted">{t("features.noRequirements")}</p>
      )}
    </div>
  );
}
